import { useEffect, useRef, useState } from "react"
import { Send, RotateCw, MessagesSquare, Check } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ForgeButton } from "@/components/ui/forge-button"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/cn"
import { TestConversationMessageCard, TestConversationThinkingRow } from "@/components/domain/test-conversation-message-card"
import type { ChatBubble, TestLabAgentOption, WorkflowTraceEvent, WorkflowTracePayload } from "@/types/test_lab"

type TestConvoForgeMode = "discuss" | "test_case_exact" | "test_case_liberties"

type Props = {
  skillId: number
  skillKey: string
  /** SkillVersion id to pin the test convo to when scope === "skill_only".
      Backend (StartAgentTestConvo → AgentConvo.test_skill_version_ids →
      PromptComposer) keys off SkillVersion ids; passing the parent Skill
      id here would filter to nothing. Parent passes `skill.versionId`
      (the working version: draft || live || latest). */
  defaultSkillVersionId: number
  /** Agent options the operator can pick from (the company's agents). */
  agents: TestLabAgentOption[]
  /** Default agent (skill's primary attachment, or first company agent). */
  defaultAgentId: number | null
  /** Per-staff persisted source_key for THIS skill. Hydrates the field on
   *  load and is auto-updated by `PATCH /skills/:id/test/source_key` as
   *  the operator types (debounced). */
  initialSourceKey?: string
  /** Forge actions fetch a synthesized message, switch to the Design tab,
      and seed the composer. */
  onDiscussConvo?: (convoId: number, mode?: TestConvoForgeMode) => void
  onWorkflowTrace?: (trace: WorkflowTracePayload | null) => void
}

type ConvoState =
  | { kind: "idle" }
  | { kind: "starting" }
  | { kind: "ready"; convoId: number; convoUuid: string; appuserId: number }
  | { kind: "error"; message: string }

export function TestPane({ skillId, skillKey, defaultSkillVersionId, agents, defaultAgentId, initialSourceKey = "", onDiscussConvo, onWorkflowTrace }: Props) {
  const [agentId, setAgentId]       = useState<number | null>(defaultAgentId)
  const [scope, setScope]           = useState<"skill_only" | "all_skills">("skill_only")
  const [convo, setConvo]           = useState<ConvoState>({ kind: "idle" })
  const [messages, setMessages]     = useState<ChatBubble[]>([])
  const [draft, setDraft]           = useState("")
  const [sending, setSending]       = useState(false)
  const [resetNonce, setResetNonce] = useState(0)
  // Optional source_key — when present, the test convo runs as that
  // existing appuser (banned, locked, has-payouts, etc.). Empty string
  // → fresh ephemeral redteam-prefixed appuser per session. Hydrated
  // from CompanyUser.test_lab_source_keys[skill_id].
  const [sourceKey, setSourceKey]       = useState<string>(initialSourceKey)
  // Buffered draft of the source-key field — committed to `sourceKey`
  // (and persisted server-side) on a 600ms debounce after typing stops,
  // or immediately on blur / Enter. No Apply button. Typing without
  // debouncing would respawn the convo on every keystroke.
  const [sourceKeyDraft, setSourceKeyDraft] = useState<string>(initialSourceKey)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  // Keep the active convo in a ref so async send/refetch paths always see
  // the current convoId, not the stale "idle" / "starting" value from the
  // first render.
  const convoRef = useRef<ConvoState>({ kind: "idle" })
  useEffect(() => { convoRef.current = convo }, [convo])

  // Source-key autosave status — drives the inline green-check indicator.
  // "saved" auto-clears after 1.5s so the check doesn't linger forever.
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle")
  useEffect(() => {
    if (saveStatus !== "saved") return
    const t = window.setTimeout(() => setSaveStatus("idle"), 1500)
    return () => window.clearTimeout(t)
  }, [saveStatus])

  // Source-key autosave: debounce typing → commit to sourceKey (which
  // respawns the convo) AND persist server-side. Blur / Enter call
  // commitSourceKey directly (no wait). Empty value clears the saved
  // entry, matching the controller's delete-on-empty behavior.
  function commitSourceKey(next: string) {
    const trimmed = next.trim()
    setSourceKeyDraft(trimmed)
    setSourceKey(trimmed)
    setSaveStatus("saving")
    const body = new URLSearchParams()
    body.set("source_key", trimmed)
    fetch(`/skills/${skillId}/test/source_key`, {
      method: "PATCH", headers: csrfHeaders(), credentials: "same-origin", body,
    })
      .then((r) => { if (r.ok) setSaveStatus("saved"); else setSaveStatus("idle") })
      .catch(() => setSaveStatus("idle"))
  }
  // Debounce committing while the user is still typing.
  useEffect(() => {
    if (sourceKeyDraft === sourceKey) return
    const t = window.setTimeout(() => commitSourceKey(sourceKeyDraft), 600)
    return () => window.clearTimeout(t)
  // commitSourceKey is referentially stable enough — the only reactive
  // input is sourceKeyDraft. Re-running on sourceKey change is fine.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKeyDraft, sourceKey])

  // Spin up a fresh ephemeral convo whenever agentId/scope changes or
  // the operator hits Reset. Previous convo is orphaned (redteam-
  // prefixed appuser, so it won't pollute production analytics).
  useEffect(() => {
    if (!agentId) {
      setConvo({ kind: "idle" })
      setMessages([])
      onWorkflowTrace?.(null)
      return
    }
    setConvo({ kind: "starting" })
    setMessages([])
    onWorkflowTrace?.(null)
    const body = new URLSearchParams()
    body.set("agent_id", String(agentId))
    // skill_ids[] holds SkillVersion ids — StartAgentTestConvo writes them
    // into agent_convos.test_skill_version_ids, which PromptComposer
    // resolves via SkillVersion.where(id: ...). Passing a parent Skill id
    // here would filter the prompt down to NO skills (silent breakage).
    if (scope === "skill_only") body.append("skill_ids[]", String(defaultSkillVersionId))
    if (sourceKey.trim()) body.set("source_key", sourceKey.trim())
    fetch(`/skills/${skillId}/test/agent_chat/start`, {
      method: "POST", headers: csrfHeaders(), credentials: "same-origin", body,
    })
      .then(async (r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then((d) => {
        setConvo({ kind: "ready", convoId: d.convo_id, convoUuid: d.convo_uuid, appuserId: d.appuser_id })
        refetchAgentChat(skillId, d.convo_id).then((fresh) => {
          setMessages(fresh.messages)
          onWorkflowTrace?.(fresh.workflowTrace)
        })
      })
      .catch((e) => setConvo({ kind: "error", message: e.message }))
  }, [agentId, scope, skillId, defaultSkillVersionId, resetNonce, sourceKey, onWorkflowTrace])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages.length])

  async function send() {
    if (sending || !draft.trim() || convo.kind !== "ready") return
    setSending(true)
    const text = draft
    setDraft("")
    const optimistic: ChatBubble = {
      id: -Date.now(),
      role: "user",
      content: text,
      createdAt: new Date().toISOString(),
    }
    setMessages((m) => [...m, optimistic])
    const body = new URLSearchParams({ convo_id: String(convo.convoId), content: text })
    try {
      await fetch(`/skills/${skillId}/test/agent_chat`, {
        method: "POST", headers: csrfHeaders(), credentials: "same-origin", body,
      })
      // The controller blocks until TurnLoop completes — by the time we
      // get here, BOTH the user-turn AND assistant-turn AgentMessages are
      // already in the DB. Refetch immediately so this pane doesn't need a
      // company-wide realtime subscription.
      const c = convoRef.current
      if (c.kind === "ready") {
        const fresh = await refetchAgentChat(skillId, c.convoId)
        setMessages(fresh.messages)
        onWorkflowTrace?.(fresh.workflowTrace)
      }
    } finally {
      setSending(false)
    }
  }

  function resetConvo() {
    if (!confirm("Start a fresh ephemeral convo? Current messages will be discarded.")) return
    setResetNonce((n) => n + 1)
  }

  const scopeLabel   = scope === "skill_only" ? `Only ${skillKey}` : "All skills"
  const activeAgent  = agents.find((a) => a.id === agentId) ?? null
  // The controller blocks until TurnLoop finishes, then we refetch the
  // transcript. A persisted user message with no assistant reply can be a
  // real failure state, so the spinner tracks only the in-flight POST.
  const pendingReply = sending

  return (
    <div className="flex flex-col gap-3">
      {/* Header strip */}
      <Card className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-2">
          <MessagesSquare size={14} strokeWidth={1.7} className="text-app-muted" />
          <div>
            <h3 className="text-sm font-semibold">Test against the agent</h3>
            <p className="text-[10px] text-app-subtle">
              {activeAgent ? `Talking to ${activeAgent.name}` : "Pick an agent to start"} · {scopeLabel}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={agentId ?? ""}
            onChange={(e) => setAgentId(e.target.value ? Number(e.target.value) : null)}
            className="rounded-control border border-border bg-app-panel2 px-2 py-1 text-xs text-app-text"
            aria-label="Pick agent"
          >
            {agents.length === 0 && <option value="">No agents in company</option>}
            {agents.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <ScopeToggle value={scope} onChange={setScope} />
          {/* Source-key input — empty means a fresh ephemeral redteam-
              prefixed appuser; filled means re-use that real appuser
              so the convo runs against their actual state. We only
              commit on Apply / Enter so we don't respawn a convo on
              every keystroke. */}
          <div className="relative inline-flex items-center">
            <input
              type="text"
              value={sourceKeyDraft}
              onChange={(e) => setSourceKeyDraft(e.target.value)}
              onBlur={() => commitSourceKey(sourceKeyDraft)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  commitSourceKey(sourceKeyDraft)
                }
              }}
              placeholder="source_key (optional)"
              className="w-44 rounded-control border border-border bg-app-panel2 px-2 py-1 pr-7 text-xs text-app-text placeholder:text-app-subtle"
              aria-label="Run as a specific appuser by source_key — autosaves on blur"
              title="Autosaves on blur / Enter / pause. Empty clears the saved value."
            />
            {saveStatus === "saved" && (
              <Check
                size={14}
                strokeWidth={2.5}
                className="pointer-events-none absolute right-2 text-status-success"
                aria-label="Saved"
              />
            )}
          </div>
          <ForgeButton
            size="sm"
            onClick={() => convo.kind === "ready" && onDiscussConvo?.(convo.convoId, "discuss")}
            disabled={convo.kind !== "ready" || messages.length === 0 || !onDiscussConvo}
            title={
              convo.kind !== "ready" ? "Start a convo first"
              : messages.length === 0 ? "Send a message first"
              : "Open the Design tab with this convo pre-filled for Forge"
            }
          >
            Discuss with Forge
          </ForgeButton>
          <Button type="button" size="sm" variant="ghost" onClick={resetConvo} disabled={convo.kind === "starting"}>
            <RotateCw size={12} strokeWidth={2} className="mr-1" /> Reset convo
          </Button>
        </div>
      </Card>

      {/* Chat thread */}
      <Card className="flex flex-col p-0">
        <div ref={scrollRef} className="flex-1 max-h-[min(60vh,640px)] space-y-1.5 overflow-y-auto px-4 py-2">
          {convo.kind === "starting" && <p className="px-2 py-4 text-xs text-app-muted">Spinning up ephemeral convo…</p>}
          {convo.kind === "error"    && <p className="px-2 py-4 text-xs text-status-error">{convo.message}</p>}
          {convo.kind === "idle" && agentId === null && (
            <p className="px-2 py-4 text-xs text-app-muted">Pick an agent above to begin.</p>
          )}
          {convo.kind === "ready" && messages.length === 0 && (
            <p className="px-2 py-4 text-xs text-app-muted">Type as the user — the agent's reply appears when the turn completes.</p>
          )}
          {messages.map((m) => (
            <TestConversationMessageCard
              key={m.id}
              msg={withIdentity(m, activeAgent)}
              userLabel="User"
              assistantLabel={activeAgent?.name ?? "Agent"}
            />
          ))}
          {pendingReply && <TestConversationThinkingRow label={activeAgent?.name ?? "Agent"} />}
        </div>

        {convo.kind === "ready" && messages.length > 0 && onDiscussConvo && (
          <div className="flex flex-wrap items-center gap-2 border-t border-border px-3 py-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => onDiscussConvo(convo.convoId, "test_case_exact")}
              title="Seed Forge with instructions to preserve this transcript, source context, ordering, and observed tool/endpoint diagnostics."
            >
              Create test case exactly
            </Button>
            <ForgeButton
              size="sm"
              onClick={() => onDiscussConvo(convo.convoId, "test_case_liberties")}
              title="Seed Forge with instructions to simplify this into the smallest useful test for the observed failure."
            >
              Simplified Test Case with Forge
            </ForgeButton>
          </div>
        )}

        <footer className="border-t border-border p-3">
          <div className="flex gap-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  send()
                }
              }}
              placeholder={convo.kind === "ready" ? "Type as the user…" : "Setting up…"}
              rows={2}
              disabled={convo.kind !== "ready"}
              className="flex-1"
            />
            <Button
              type="button" variant="red" size="md"
              onClick={send}
              disabled={convo.kind !== "ready" || !draft.trim() || sending}
              className="self-end"
            >
              <Send size={16} strokeWidth={2} />
            </Button>
          </div>
        </footer>
      </Card>
    </div>
  )
}

// Decorate a ChatBubble with display identity. User-role turns are
// operator-authored but intentionally labelled as the emulated user.
function withIdentity(
  m: ChatBubble,
  agent: TestLabAgentOption | null,
): ChatBubble {
  if (m.role === "user") {
    // Test Lab messages are operator-authored, but they are sent while
    // emulating an end user. Label the transcript as the user persona
    // instead of the staff account so red-team lines read correctly.
    return { ...m, authorName: "User", avatarUrl: null }
  }
  if (m.role === "assistant" && agent) {
    return { ...m, authorName: agent.name, avatarUrl: agent.avatarUrl ?? null }
  }
  return m
}

function ScopeToggle({ value, onChange }: { value: "skill_only" | "all_skills"; onChange: (v: "skill_only" | "all_skills") => void }) {
  return (
    <div className="inline-flex overflow-hidden rounded-control border border-border text-[11px]">
      <button
        type="button"
        onClick={() => onChange("skill_only")}
        className={cn("px-2 py-1", value === "skill_only" ? "bg-brand-red text-white" : "bg-app-panel2 text-app-muted hover:text-app-text")}
      >
        Only this skill
      </button>
      <button
        type="button"
        onClick={() => onChange("all_skills")}
        className={cn("px-2 py-1", value === "all_skills" ? "bg-brand-red text-white" : "bg-app-panel2 text-app-muted hover:text-app-text")}
      >
        All skills
      </button>
    </div>
  )
}

async function refetchAgentChat(skillId: number, convoId: number): Promise<{ messages: ChatBubble[]; workflowTrace: WorkflowTracePayload | null }> {
  const res = await fetch(`/skills/${skillId}/test/agent_chat/messages?convo_id=${convoId}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  })
  if (!res.ok) return { messages: [], workflowTrace: null }
  const data = (await res.json()) as { messages: ChatBubble[]; workflowTrace?: WorkflowTracePayload | null }
  const workflowTrace = data.workflowTrace ?? null
  return { messages: mergeTraceDiagnostics(data.messages ?? [], workflowTrace), workflowTrace }
}

function mergeTraceDiagnostics(messages: ChatBubble[], workflowTrace: WorkflowTracePayload | null): ChatBubble[] {
  const diagnostics = workflowTraceDiagnosticMessages(workflowTrace)
  if (diagnostics.length === 0) return messages

  return [...messages, ...diagnostics].sort((a, b) => {
    const at = Date.parse(a.createdAt)
    const bt = Date.parse(b.createdAt)
    if (Number.isFinite(at) && Number.isFinite(bt) && at !== bt) return at - bt
    return a.id - b.id
  })
}

function workflowTraceDiagnosticMessages(workflowTrace: WorkflowTracePayload | null): ChatBubble[] {
  const events = Array.isArray(workflowTrace?.trace) ? workflowTrace.trace : []
  const seen = new Set<string>()
  return events.flatMap((event) => {
    const msg = workflowTraceDiagnosticMessage(event)
    if (!msg) return []
    const signature = [
      msg.turnNumber ?? "",
      msg.toolCalls?.[0] ? stringish(asRecord(msg.toolCalls[0]).name) : "",
      stringish(asRecord(asRecord(msg.toolCalls?.[0]).input).endpoint_key),
      stringish(asRecord(asRecord(msg.toolCalls?.[0]).input).action_key),
      stringish(asRecord(msg.toolResults?.[0]).is_error),
      payloadText(asRecord(msg.toolResults?.[0]).output),
    ].join("|")
    if (seen.has(signature)) return []
    seen.add(signature)
    return [msg]
  })
}

function workflowTraceDiagnosticMessage(event: WorkflowTraceEvent): ChatBubble | null {
  const details = asRecord(event.details)
  const result = asRecord(details.result)
  const owner = stringish(details.owner)
  const eventName = event.event
  const endpoint = stringish(details.endpoint) || stringish(result.endpoint)
  const tool = stringish(details.tool) || stringish(result.tool)
  const actionKey = stringish(details.action_key) || stringish(details.action)
  const status = stringish(result.status) || stringish(details.status)
  const isFailure = ["failed", "error", "errored"].includes((status || "").toLowerCase()) ||
    eventName.includes("failed") ||
    eventName.includes("error") ||
    eventName.includes("rejected") ||
    eventName === "dropped_unavailable_tool_calls"

  const visibleAction =
    eventName === "action_completed" ||
    eventName === "action_failed" ||
    eventName === "workflow_error" ||
    eventName === "dropped_unavailable_tool_calls" ||
    eventName === "workflow_v3_action_rejected"
  if (!visibleAction) return null
  if (owner === "tool_dispatch") return null

  const diagnosticOwner = ["workflow_v3_action", "workflow_v3_data", "skill_card_data"].includes(owner || "")
  if (!diagnosticOwner && !isFailure && !endpoint && !tool) return null
  if (owner === "workflow_v3_policy") return null

  const name = endpoint ? "call_company_endpoint" : tool || actionKey || eventName
  const outputResult = result.data ?? result
  const request = endpoint ? endpointRequestMetadata(outputResult) : null
  const output = endpoint ? {
    result: simplifyEndpointDiagnosticResult(outputResult),
    request,
  } : {
    event: eventName,
    owner,
    status: status || (isFailure ? "failed" : "completed"),
    action_key: actionKey,
    endpoint,
    tool,
    result: outputResult,
    error: details.error ?? result.error,
  }
  const input = {
    action_key: actionKey,
    endpoint_key: endpoint,
    tool,
    params: details.input ?? details.params,
  }

  return {
    id: -Math.max(1, Number(event.seq) || Date.now()),
    role: "tool",
    content: null,
    toolCalls: [{ id: `trace-${event.seq}`, name, input: compactObject(input) }],
    toolResults: [{ id: `trace-${event.seq}`, name, output: compactObject(output), is_error: isFailure }],
    turnNumber: event.turn_number,
    createdAt: event.at || new Date().toISOString(),
  }
}

function compactObject(value: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => {
    if (v == null) return false
    if (typeof v === "string" && v.trim() === "") return false
    if (typeof v === "object" && !Array.isArray(v) && Object.keys(v as Record<string, unknown>).length === 0) return false
    return true
  }))
}

function endpointRequestMetadata(value: unknown): unknown {
  const result = asRecord(value)
  return result.request ?? null
}

function simplifyEndpointDiagnosticResult(value: unknown): unknown {
  const result = asRecord(value)
  const upstreamBody = result.upstream_body ?? result.body
  const statusCode = result.status_code ?? result.status
  const { request: _request, ...resultWithoutRequest } = result
  if (upstreamBody == null && statusCode == null) return resultWithoutRequest

  return compactObject({
    status: statusCode,
    body: upstreamBody ?? {},
  })
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

function stringish(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  return null
}

function payloadText(value: unknown): string {
  try {
    return JSON.stringify(value ?? {})
  } catch {
    return String(value)
  }
}

function csrfHeaders(): HeadersInit {
  const token = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content
  return {
    "X-CSRF-Token": token ?? "",
    "Content-Type": "application/x-www-form-urlencoded",
    "Accept": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  }
}
